import React from 'react';
import { MainCard, MainCardTitle } from 'src/components/main-card-type.tsx';
import { normalSanatizerBRL } from "src/utils/functions";

interface RentVsBuyResults {
  imovel: string;
  totalAluguel: string;
  financiamento: string;
  patrimonioFinanciando: string;
  patrimonioAlugando: string;
  prazo: number;
  bestChoice: string;
  totalAportesFinanciando: string;
  totalAportesAlugando: string;
  rentabilidade: number;
  valorizacao: number;
  igpm: number;
  meses: number[];
  prestacoes: number[];
  alugueis: number[];
  saldosDevedores: number[];
  aportesFinanciando: number[];
  aportesAlugando: number[];
}

export const RentVsBuyResponse: React.FC<{ results: RentVsBuyResults }> = ({ results }) => {
  return (
    <MainCard className="mt-4">
      <MainCardTitle>De acordo com as nossas contas, a melhor opção é:</MainCardTitle>
      <h2 className="text-2xl font-bold text-center text-primary mb-6">{results.bestChoice}</h2>
      <div className="grid grid-cols-3 gap-4 mb-8 text-center">
        <div className="border rounded p-4">
          <h3 className="text-lg font-semibold text-red-600 mb-2">Resultado só financiando</h3>
          <div className="space-y-4">
            <div>
              <p className="font-semibold">Patrimônio total</p>
              <p className="text-xl font-bold text-red-600">R$ {normalSanatizerBRL(parseFloat(results.imovel))}</p>
              <p className="text-xs font-bold">Valor do imóvel em {results.prazo / 12} anos</p>
            </div>
            <div>
              <p className="font-semibold">Total pago financiando</p>
              <p className="text-xl font-bold text-red-600">R$ {normalSanatizerBRL(parseFloat(results.financiamento))}</p>
              <p className="text-xs font-bold">Usando a tabela SAC</p>
            </div>
            <div>
              <p className="font-semibold">Total aportado</p>
              <p className="text-xl font-bold text-red-600">R$ 0,00</p>
              <p className="text-xs font-bold">Neste caso você não aporta nada</p>
            </div>
          </div>
        </div>
        <div className="border rounded p-4 border-green-500">
          <h3 className="text-lg font-semibold text-green-600 mb-2">Resultado financiando e investindo</h3>
          <div className="space-y-4">
            <div>
              <p className="font-semibold">Patrimônio total</p>
              <p className="text-xl font-bold text-green-600">R$ {normalSanatizerBRL(parseFloat(results.patrimonioFinanciando))}</p>
              <p className="text-xs font-bold">Valor do imóvel + investimentos em {results.prazo / 12} anos</p>
            </div>
            <div>
              <p className="font-semibold">Total pago financiando</p>
              <p className="text-xl font-bold text-green-600">R$ {normalSanatizerBRL(parseFloat(results.financiamento))}</p>
              <p className="text-xs font-bold">Usando a tabela SAC</p>
            </div>
            <div>
              <p className="font-semibold">Total aportado</p>
              <p className="text-xl font-bold text-green-600">R$ {normalSanatizerBRL(parseFloat(results.totalAportesFinanciando))}</p>
              <p className="text-xs font-bold">A soma dos aportes financiando</p>
            </div>
          </div>
        </div>
        <div className="border rounded p-4">
          <h3 className="text-lg font-semibold text-yellow-600 mb-2">Resultado alugando e investindo</h3>
          <div className="space-y-4">
            <div>
              <p className="font-semibold">Patrimônio total</p>
              <p className="text-xl font-bold text-yellow-600">R$ {normalSanatizerBRL(parseFloat(results.patrimonioAlugando))}</p>
              <p className="text-xs font-bold">Usando uma rentabilidade de {results.rentabilidade}% a.a.</p>
            </div>
            <div>
              <p className="font-semibold">Total pago de aluguel</p>
              <p className="text-xl font-bold text-yellow-600">R$ {normalSanatizerBRL(parseFloat(results.totalAluguel))}</p>
              <p className="text-xs font-bold">Com o reajuste indicado de {results.igpm}%</p>
            </div>
            <div>
              <p className="font-semibold">Total aportado</p>
              <p className="text-xl font-bold text-yellow-600">R$ {normalSanatizerBRL(parseFloat(results.totalAportesAlugando))}</p>
              <p className="text-xs font-bold">A soma dos aportes alugando</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-h-[400px] overflow-y-auto m-auto overflow-x-auto">
        <table className="bg-white rounded w-full max-h-[400px] overflow-y-auto">
          <thead className="bg-gray-200 sticky top-0">
            <tr>
              <th className="px-4 py-2 text-center truncate">Mês</th>
              <th className="px-4 py-2 text-center truncate">Prestação</th>
              <th className="px-4 py-2 text-center truncate">Aluguel</th>
              <th className="px-4 py-2 text-center truncate">Saldo Devedor</th>
              <th className="px-4 py-2 text-center truncate">Aportes financiando</th>
              <th className="px-4 py-2 text-center truncate">Aportes Alugando</th>
            </tr>
          </thead>
          <tbody className="max-h-[400px] overflow-y-auto">
            {results.meses.map((mes, index) => (
              <tr className="border-b" key={index}>
                <td className="px-4 py-2 text-center">{mes}</td>
                <td className="px-4 py-2 text-center">
                  <span className="text-sm mr-1">R$</span>
                  {normalSanatizerBRL(results.prestacoes[index])}
                </td>
                <td className="px-4 py-2 text-center bg-blue-50">
                  <span className="text-sm mr-1">R$</span>
                  {normalSanatizerBRL(results.alugueis[index])}
                </td>
                <td className="px-4 py-2 text-center bg-green-50">
                  <span className="text-sm mr-1">R$</span>
                  {normalSanatizerBRL(results.saldosDevedores[index])}
                </td>
                <td className="px-4 py-2 text-center bg-gray-200">
                  <span className="text-sm mr-1">R$</span>
                  {normalSanatizerBRL(results.aportesFinanciando[index])}
                </td>
                <td className="px-4 py-2 text-center">
                  <span className="text-sm mr-1">R$</span>
                  {normalSanatizerBRL(results.aportesAlugando[index])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </MainCard>
  );
};
